import React, { useState, useEffect } from 'react';
import { LOCStatus, LOCApplication } from '../data/mockData';
import { formatCurrency } from '../theme';
import StatusBadge from '../components/StatusBadge';
import api from '../services/api';

const statusFilters: { label: string; value: LOCStatus | 'all' }[] = [
  { label: 'All', value: 'all' },
  { label: 'Submitted', value: 'submitted' },
  { label: 'Under Review', value: 'under_review' },
  { label: 'Approved', value: 'approved' },
  { label: '80% Paid', value: 'payment_80_done' },
  { label: 'Settlement', value: 'final_settlement' },
  { label: 'Completed', value: 'completed' },
  { label: 'Rejected', value: 'rejected' },
];

const Applications: React.FC = () => {
  const [allApplications, setAllApplications] = useState<LOCApplication[]>([]);
  const [filter, setFilter] = useState<LOCStatus | 'all'>('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [detailDocs, setDetailDocs] = useState<any[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'info' | 'documents' | 'settlement'>('info');
  const [settlement, setSettlement] = useState<any>(null);
  const [settlementLoading, setSettlementLoading] = useState(false);

  const fetchApplications = () => {
    setLoading(true);
    api.loc.getAll()
      .then(res => {
        const data = res.data || res;
        if (Array.isArray(data)) setAllApplications(data);
      })
      .catch((e) => setError(e.message || 'Failed to load data'))
      .finally(() => setLoading(false));
  };

  const handleApprove = async (id: string) => {
    setActionLoading(id);
    try {
      await api.approvals.approve(id);
      fetchApplications();
    } catch (err: any) {
      alert('Approve failed: ' + (err.message || 'Unknown error'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleApproveSettlement = async (id: string) => {
    setActionLoading(id);
    try {
      await api.approvals.approveSettlement(id);
      fetchApplications();
      setSelected(null);
    } catch (err: any) {
      alert('Settlement approval failed: ' + (err.message || 'Unknown error'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (id: string) => {
    setActionLoading(id);
    try {
      await api.approvals.reject(id, 'Rejected by admin');
      fetchApplications();
    } catch (err: any) {
      alert('Reject failed: ' + (err.message || 'Unknown error'));
    } finally {
      setActionLoading(null);
    }
  };

  useEffect(() => {
    if (!selected) { setDetailDocs([]); setSettlement(null); return; }
    setDocsLoading(true);
    setSettlementLoading(true);
    setActiveTab('info');
    api.documents.getByEntity('loc', selected)
      .then(res => {
        const data = Array.isArray(res) ? res : (res as any)?.data || [];
        setDetailDocs(data);
      })
      .catch(() => setDetailDocs([]))
      .finally(() => setDocsLoading(false));
    api.approvals.getSettlementSummary(selected)
      .then(res => setSettlement(res))
      .catch(() => setSettlement(null))
      .finally(() => setSettlementLoading(false));
  }, [selected]);

  useEffect(() => {
    fetchApplications();
  }, []);

  const filtered = allApplications.filter(l => {
    if (filter !== 'all' && l.status !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      return l.refNumber.toLowerCase().includes(q) ||
        l.officerName.toLowerCase().includes(q) ||
        l.hospitalName.toLowerCase().includes(q) ||
        l.beneficiaryName.toLowerCase().includes(q);
    }
    return true;
  });

  const detail = selected ? allApplications.find(l => l.id === selected) : null;

  return (
    <>
      <div className="header">
        <div className="header-left">
          <div>
            <h2>LOC Applications</h2>
            <p>{allApplications.length} total applications</p>
          </div>
        </div>
        <div className="header-right">
          <div className="search-bar">
            <span>🔍</span>
            <input placeholder="Search by ref, officer, hospital..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <button className="header-btn primary">+ Export CSV</button>
        </div>
      </div>

      <div className="content">
        {loading && <div style={{ padding: 40, textAlign: 'center', fontSize: 16, color: '#94A3B8' }}>Loading...</div>}
        {error && <div style={{ padding: '12px 20px', background: 'rgba(239,68,68,0.1)', color: '#EF4444', borderRadius: 8, margin: '0 0 16px', fontSize: 13, fontWeight: 600 }}>{error}</div>}
        <div className="card">
          {/* Filters */}
          <div style={{ padding: '12px 20px', borderBottom: '1px solid #E8ECF2' }}>
            <div className="filter-bar">
              {statusFilters.map(f => (
                <button
                  key={f.value}
                  className={`filter-chip${filter === f.value ? ' active' : ''}`}
                  onClick={() => setFilter(f.value)}
                >
                  {f.label}
                  {f.value !== 'all' && (
                    <span style={{ marginLeft: 4, opacity: 0.6 }}>
                      ({allApplications.filter(l => l.status === f.value).length})
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Table */}
          <div className="card-body">
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Reference</th>
                    <th>Officer</th>
                    <th>Beneficiary</th>
                    <th>Hospital</th>
                    <th>Treatment</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Submitted</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(loc => (
                    <tr key={loc.id} style={{ cursor: 'pointer', background: selected === loc.id ? '#F7F8FA' : undefined }}
                      onClick={() => setSelected(loc.id)}>
                      <td>
                        <div style={{ fontWeight: 700, fontSize: 12 }}>{loc.refNumber}</div>
                        {loc.isEmergency && <span className="badge emergency" style={{ marginTop: 2 }}>
                          <span className="badge-dot" />EMERGENCY
                        </span>}
                      </td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{loc.officerName}</div>
                        <div style={{ fontSize: 11, color: '#94A3B8' }}>{loc.category} · {loc.officerCfmsId}</div>
                      </td>
                      <td>
                        <div>{loc.beneficiaryName}</div>
                        <div style={{ fontSize: 11, color: '#94A3B8' }}>{loc.relation}</div>
                      </td>
                      <td>
                        <div>{loc.hospitalName}</div>
                        <div style={{ fontSize: 11, color: '#94A3B8' }}>{loc.hospitalCity} · Tier {loc.hospitalTier}</div>
                      </td>
                      <td>
                        <div style={{ fontSize: 12 }}>{loc.treatment}</div>
                        <div style={{ fontSize: 11, color: '#94A3B8' }}>{loc.diagnosis}</div>
                      </td>
                      <td className="amount">{formatCurrency(loc.estimatedAmount)}</td>
                      <td><StatusBadge status={loc.status} /></td>
                      <td style={{ fontSize: 12, color: '#94A3B8' }}>
                        {new Date(loc.submittedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })}
                      </td>
                      <td onClick={e => e.stopPropagation()}>
                        {(loc.status === 'submitted' || loc.status === 'under_review') && (
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button className="action-btn approve" disabled={actionLoading === loc.id} onClick={() => handleApprove(loc.id)}>
                              {actionLoading === loc.id ? '...' : '✓ Approve'}
                            </button>
                            <button className="action-btn reject" disabled={actionLoading === loc.id} onClick={() => handleReject(loc.id)}>
                              {actionLoading === loc.id ? '...' : '✕ Reject'}
                            </button>
                          </div>
                        )}
                        {loc.status === 'final_settlement' && (
                          <button className="action-btn approve" disabled={actionLoading === loc.id} onClick={() => handleApproveSettlement(loc.id)}>
                            {actionLoading === loc.id ? '...' : '✓ Settle'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pagination">
              <div className="pagination-info">Showing {filtered.length} of {allApplications.length} applications</div>
              <div className="pagination-btns">
                <button className="page-btn active">1</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {detail && (
        <div className="modal-overlay" onClick={() => setSelected(null)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: '#fff',
            borderRadius: 16,
            width: 720,
            maxWidth: '92vw',
            maxHeight: '88vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 24px 80px rgba(10,22,40,0.25)',
            overflow: 'hidden',
          }}>
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '18px 24px',
              borderBottom: '1px solid #E8ECF2',
              flexShrink: 0,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <h3 style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>{detail.refNumber}</h3>
                <StatusBadge status={detail.status} />
                {detail.isEmergency && (
                  <span className="badge emergency"><span className="badge-dot" />EMERGENCY</span>
                )}
              </div>
              <button className="modal-close" onClick={() => setSelected(null)}>✕</button>
            </div>

            {/* Tabs */}
            <div style={{
              display: 'flex',
              gap: 0,
              borderBottom: '1px solid #E8ECF2',
              padding: '0 24px',
              flexShrink: 0,
            }}>
              {(['info', 'documents', ...(detail.status === 'final_settlement' || detail.status === 'completed' ? ['settlement'] as const : [])] as const).map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  style={{
                    padding: '12px 20px',
                    fontSize: 13,
                    fontWeight: 600,
                    color: activeTab === tab ? '#1E293B' : '#94A3B8',
                    background: 'none',
                    border: 'none',
                    borderBottom: activeTab === tab ? '2px solid #C8A951' : '2px solid transparent',
                    cursor: 'pointer',
                    textTransform: 'capitalize',
                    transition: 'all 0.15s',
                  }}
                >
                  {tab === 'documents' ? `Documents${detailDocs.length > 0 ? ` (${detailDocs.length})` : ''}` : tab === 'settlement' ? 'Settlement' : 'Application Info'}
                </button>
              ))}
            </div>

            {/* Modal Body */}
            <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>
              {activeTab === 'info' && (
                <>
                  {/* Amount Banner */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '14px 18px',
                    background: 'linear-gradient(135deg, #F8FAFC 0%, #EEF2FF 100%)',
                    borderRadius: 10,
                    marginBottom: 20,
                  }}>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5 }}>Estimated Amount</div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: '#1E293B' }}>{formatCurrency(detail.estimatedAmount)}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 16 }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 10, fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase' }}>80% Advance</div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: '#059669' }}>{formatCurrency(detail.estimatedAmount * 0.8)}</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 10, fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase' }}>20% Final</div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: '#6366F1' }}>{formatCurrency(detail.estimatedAmount * 0.2)}</div>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                    {/* Left Column */}
                    <div>
                      <h4 style={{ fontSize: 12, fontWeight: 700, color: '#94A3B8', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Application Info</h4>
                      <div style={{ background: '#F8FAFC', borderRadius: 10, padding: 16 }}>
                        {[
                          ['Officer', `${detail.officerName} (${detail.category})`],
                          ['CFMS ID', detail.officerCfmsId],
                          ['Beneficiary', `${detail.beneficiaryName} (${detail.relation})`],
                          ['Type', detail.isEmergency ? 'EMERGENCY SOS' : 'Standard Cashless'],
                        ].map(([label, value]) => (
                          <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #EEF0F4' }}>
                            <span style={{ fontSize: 12, color: '#94A3B8', fontWeight: 500 }}>{label}</span>
                            <span style={{ fontSize: 13, fontWeight: 600, color: '#1E293B', textAlign: 'right', maxWidth: '60%' }}>{value}</span>
                          </div>
                        ))}
                      </div>

                      <h4 style={{ fontSize: 12, fontWeight: 700, color: '#94A3B8', marginBottom: 10, marginTop: 18, textTransform: 'uppercase', letterSpacing: 0.5 }}>Hospital & Treatment</h4>
                      <div style={{ background: '#F8FAFC', borderRadius: 10, padding: 16 }}>
                        {[
                          ['Hospital', `${detail.hospitalName}, ${detail.hospitalCity}`],
                          ['Tier', `Tier ${detail.hospitalTier}`],
                          ['Diagnosis', detail.diagnosis],
                          ['Treatment', detail.treatment],
                        ].map(([label, value]) => (
                          <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #EEF0F4' }}>
                            <span style={{ fontSize: 12, color: '#94A3B8', fontWeight: 500 }}>{label}</span>
                            <span style={{ fontSize: 13, fontWeight: 600, color: '#1E293B', textAlign: 'right', maxWidth: '60%' }}>{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Right Column */}
                    <div>
                      <h4 style={{ fontSize: 12, fontWeight: 700, color: '#94A3B8', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Verification</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 18 }}>
                        {[
                          { label: 'PAN', ok: detail.panVerified },
                          { label: 'GST', ok: detail.gstVerified },
                          { label: 'Bank', ok: detail.bankVerified },
                          { label: `Match: ${detail.matchScore != null ? `${detail.matchScore}%` : 'N/A'}`, ok: true },
                        ].map(v => (
                          <div key={v.label} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '10px 12px',
                            background: v.ok ? '#F0FDF4' : '#FFF7ED',
                            borderRadius: 8,
                            border: `1px solid ${v.ok ? '#BBF7D0' : '#FED7AA'}`,
                          }}>
                            <div style={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              background: v.ok ? '#22C55E' : '#F59E0B',
                              flexShrink: 0,
                            }} />
                            <span style={{ fontSize: 12, fontWeight: 600, color: v.ok ? '#166534' : '#92400E' }}>
                              {v.label} {v.label.startsWith('Match') ? '' : (v.ok ? 'Verified' : 'Pending')}
                            </span>
                          </div>
                        ))}
                      </div>

                      <h4 style={{ fontSize: 12, fontWeight: 700, color: '#94A3B8', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Timeline</h4>
                      <div style={{ background: '#F8FAFC', borderRadius: 10, padding: 16 }}>
                        {[
                          ['Submitted', new Date(detail.submittedAt).toLocaleString('en-IN')],
                          ['SLA Deadline', new Date(detail.slaDeadline).toLocaleString('en-IN')],
                          ['Approved At', detail.approvedAt ? new Date(detail.approvedAt).toLocaleString('en-IN') : '—'],
                          ['Approver', detail.approver || '—'],
                        ].map(([label, value]) => (
                          <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #EEF0F4' }}>
                            <span style={{ fontSize: 12, color: '#94A3B8', fontWeight: 500 }}>{label}</span>
                            <span style={{ fontSize: 13, fontWeight: 600, color: '#1E293B' }}>{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              )}

              {activeTab === 'documents' && (
                <>
                  {docsLoading ? (
                    <div style={{ padding: 40, textAlign: 'center', color: '#94A3B8', fontSize: 14 }}>Loading documents...</div>
                  ) : detailDocs.length === 0 ? (
                    <div style={{
                      padding: 48,
                      textAlign: 'center',
                      background: '#F8FAFC',
                      borderRadius: 12,
                      border: '2px dashed #E2E8F0',
                    }}>
                      <div style={{ fontSize: 36, marginBottom: 8, opacity: 0.4 }}>&#128196;</div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: '#64748B', marginBottom: 4 }}>No Documents Uploaded</div>
                      <div style={{ fontSize: 13, color: '#94A3B8' }}>The applicant has not uploaded any documents yet</div>
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      {detailDocs.map((doc: any) => (
                        <div key={doc.id} style={{
                          border: '1px solid #E8ECF2',
                          borderRadius: 10,
                          padding: 16,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          background: '#FAFBFC',
                          transition: 'border-color 0.15s',
                        }}>
                          <div style={{
                            width: 42,
                            height: 42,
                            borderRadius: 10,
                            background: doc.mimeType?.includes('pdf') ? '#FEE2E2' : '#DBEAFE',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 13,
                            fontWeight: 800,
                            color: doc.mimeType?.includes('pdf') ? '#DC2626' : '#2563EB',
                            flexShrink: 0,
                          }}>
                            {doc.mimeType?.includes('pdf') ? 'PDF' : 'IMG'}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#1E293B', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {doc.originalName}
                            </div>
                            <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 2 }}>
                              {doc.size ? `${(doc.size / 1024).toFixed(0)} KB` : ''} · {new Date(doc.createdAt).toLocaleDateString('en-IN')}
                            </div>
                          </div>
                          <a
                            href={api.documents.getDownloadUrl(doc.id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              padding: '6px 14px',
                              background: '#EEF2FF',
                              color: '#4F46E5',
                              borderRadius: 8,
                              fontSize: 12,
                              fontWeight: 600,
                              textDecoration: 'none',
                              whiteSpace: 'nowrap',
                              transition: 'background 0.15s',
                            }}
                          >
                            View
                          </a>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {activeTab === 'settlement' && (
                <>
                  {settlementLoading ? (
                    <div style={{ padding: 40, textAlign: 'center', color: '#94A3B8', fontSize: 14 }}>Loading settlement data...</div>
                  ) : !settlement ? (
                    <div style={{ padding: 48, textAlign: 'center', background: '#F8FAFC', borderRadius: 12, border: '2px dashed #E2E8F0' }}>
                      <div style={{ fontSize: 15, fontWeight: 600, color: '#64748B' }}>No settlement data available</div>
                    </div>
                  ) : (
                    <div>
                      {/* Reconciliation Card */}
                      <div style={{ background: 'linear-gradient(135deg, #F8FAFC 0%, #EEF2FF 100%)', borderRadius: 12, padding: 24, marginBottom: 20 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 16 }}>
                          Reconciliation Summary
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
                          <div style={{ background: '#fff', borderRadius: 10, padding: 16, textAlign: 'center', border: '1px solid #E8ECF2' }}>
                            <div style={{ fontSize: 10, fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', marginBottom: 4 }}>Estimated</div>
                            <div style={{ fontSize: 20, fontWeight: 800, color: '#1E293B' }}>{formatCurrency(settlement.estimatedAmount)}</div>
                          </div>
                          <div style={{ background: '#fff', borderRadius: 10, padding: 16, textAlign: 'center', border: '1px solid #E8ECF2' }}>
                            <div style={{ fontSize: 10, fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', marginBottom: 4 }}>Actual Bill</div>
                            <div style={{ fontSize: 20, fontWeight: 800, color: '#1E293B' }}>{formatCurrency(settlement.actualAmount)}</div>
                          </div>
                          <div style={{ background: '#fff', borderRadius: 10, padding: 16, textAlign: 'center', border: '1px solid #E8ECF2' }}>
                            <div style={{ fontSize: 10, fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', marginBottom: 4 }}>80% Advance Paid</div>
                            <div style={{ fontSize: 20, fontWeight: 800, color: '#059669' }}>{formatCurrency(settlement.advancePaid)}</div>
                          </div>
                        </div>

                        {/* Breakdown Table */}
                        <div style={{ background: '#fff', borderRadius: 10, overflow: 'hidden', border: '1px solid #E8ECF2' }}>
                          {[
                            { label: 'Estimated Amount', value: formatCurrency(settlement.estimatedAmount), color: '#1E293B' },
                            { label: '80% Advance Payment', value: `- ${formatCurrency(settlement.advancePaid)}`, color: '#059669' },
                            { label: 'Actual Hospital Bill', value: formatCurrency(settlement.actualAmount), color: '#1E293B' },
                          ].map((row, i) => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 16px', borderBottom: '1px solid #F1F5F9' }}>
                              <span style={{ fontSize: 13, color: '#64748B' }}>{row.label}</span>
                              <span style={{ fontSize: 13, fontWeight: 700, color: row.color }}>{row.value}</span>
                            </div>
                          ))}
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '14px 16px',
                            background: settlement.isRefund ? '#F0FDF4' : '#FFF7ED',
                          }}>
                            <span style={{ fontSize: 14, fontWeight: 700, color: settlement.isRefund ? '#166534' : '#92400E' }}>
                              {settlement.isRefund ? 'Refund to Beneficiary' : 'Balance Payment Due'}
                            </span>
                            <span style={{ fontSize: 18, fontWeight: 800, color: settlement.isRefund ? '#059669' : '#F59E0B' }}>
                              {formatCurrency(settlement.isRefund ? settlement.refundAmount : settlement.remainingPayment)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Variance Analysis */}
                      <div style={{ background: '#F8FAFC', borderRadius: 10, padding: 16, marginBottom: 20 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>
                          Variance Analysis
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                          <div style={{ padding: '10px 14px', background: '#fff', borderRadius: 8, border: '1px solid #E8ECF2' }}>
                            <div style={{ fontSize: 11, color: '#94A3B8', marginBottom: 2 }}>Estimated vs Actual</div>
                            <div style={{ fontSize: 15, fontWeight: 700, color: settlement.actualAmount > settlement.estimatedAmount ? '#EF4444' : '#059669' }}>
                              {settlement.actualAmount > settlement.estimatedAmount ? '+' : ''}{((settlement.actualAmount - settlement.estimatedAmount) / settlement.estimatedAmount * 100).toFixed(1)}%
                            </div>
                          </div>
                          <div style={{ padding: '10px 14px', background: '#fff', borderRadius: 8, border: '1px solid #E8ECF2' }}>
                            <div style={{ fontSize: 11, color: '#94A3B8', marginBottom: 2 }}>Total Disbursement</div>
                            <div style={{ fontSize: 15, fontWeight: 700, color: '#1E293B' }}>
                              {formatCurrency(settlement.advancePaid + (settlement.isRefund ? 0 : settlement.remainingPayment))}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Documents submitted */}
                      <div style={{ background: '#F8FAFC', borderRadius: 10, padding: 16, marginBottom: 20 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
                          Settlement Documents ({detailDocs.length})
                        </div>
                        {detailDocs.length === 0 ? (
                          <div style={{ fontSize: 13, color: '#94A3B8' }}>No documents uploaded</div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {detailDocs.map((doc: any) => (
                              <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
                                <span style={{ fontSize: 11, fontWeight: 800, color: doc.mimeType?.includes('pdf') ? '#DC2626' : '#2563EB', width: 32 }}>
                                  {doc.mimeType?.includes('pdf') ? 'PDF' : 'IMG'}
                                </span>
                                <span style={{ fontSize: 13, color: '#1E293B', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {doc.originalName}
                                </span>
                                <a href={api.documents.getDownloadUrl(doc.id)} target="_blank" rel="noopener noreferrer"
                                  style={{ fontSize: 12, color: '#4F46E5', fontWeight: 600, textDecoration: 'none' }}>
                                  View
                                </a>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Modal Footer — Settlement Approval */}
            {detail.status === 'final_settlement' && settlement && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 10,
                padding: '14px 24px',
                borderTop: '1px solid #E8ECF2',
                flexShrink: 0,
                background: settlement.isRefund ? '#F0FDF4' : '#FFF7ED',
              }}>
                <div style={{ fontSize: 13, color: '#64748B' }}>
                  {settlement.isRefund
                    ? `Refund ${formatCurrency(settlement.refundAmount)} to beneficiary`
                    : `Pay remaining ${formatCurrency(settlement.remainingPayment)} to hospital`
                  }
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    className="action-btn reject"
                    style={{ padding: '9px 20px', fontSize: 13 }}
                    disabled={actionLoading === detail.id}
                    onClick={() => handleReject(detail.id)}
                  >
                    {actionLoading === detail.id ? 'Rejecting...' : '✕ Reject'}
                  </button>
                  <button
                    className="header-btn primary"
                    disabled={actionLoading === detail.id}
                    onClick={() => handleApproveSettlement(detail.id)}
                  >
                    {actionLoading === detail.id ? 'Processing...' : '✓ Approve & Complete'}
                  </button>
                </div>
              </div>
            )}

            {/* Modal Footer — Initial Approval */}
            {(detail.status === 'submitted' || detail.status === 'under_review') && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 10,
                padding: '14px 24px',
                borderTop: '1px solid #E8ECF2',
                flexShrink: 0,
              }}>
                <button
                  className="action-btn reject"
                  style={{ padding: '9px 20px', fontSize: 13 }}
                  disabled={actionLoading === detail.id}
                  onClick={() => handleReject(detail.id)}
                >
                  {actionLoading === detail.id ? 'Rejecting...' : '✕ Reject'}
                </button>
                <button
                  className="header-btn primary"
                  disabled={actionLoading === detail.id}
                  onClick={() => handleApprove(detail.id)}
                >
                  {actionLoading === detail.id ? 'Approving...' : '✓ Approve LOC'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Applications;
